import { useState, useEffect } from 'react';

// The Konami Code sequence
const KONAMI_CODE = [
  'ArrowUp', 'ArrowUp',
  'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight',
  'ArrowLeft', 'ArrowRight',
  'b', 'a'
];

/**
 * A custom hook that detects when the user enters the Konami Code
 * @param callback Function to execute when the Konami Code is entered
 * @returns Boolean indicating if the code was just activated
 */
const useKonamiCode = (callback: () => void): boolean => {
  const [keys, setKeys] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const keydownHandler = (e: KeyboardEvent) => {
      // Get the key in lowercase to make it case-insensitive
      const key = e.key.toLowerCase();
      
      // Update the keys array with the new key
      const updatedKeys = [...keys, key];
      
      // Only keep the last N keys where N is the length of the Konami Code
      if (updatedKeys.length > KONAMI_CODE.length) {
        updatedKeys.shift();
      }
      
      setKeys(updatedKeys);
      
      // Check if the keys match the Konami Code (case-insensitive)
      const konamiCodeMatches = updatedKeys.length === KONAMI_CODE.length && 
        updatedKeys.every((k, i) => k.toLowerCase() === KONAMI_CODE[i].toLowerCase());
      
      if (konamiCodeMatches) {
        setSuccess(true);
        callback();
        // Reset after a short delay
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      }
    };

    window.addEventListener('keydown', keydownHandler);
    
    return () => {
      window.removeEventListener('keydown', keydownHandler);
    };
  }, [callback, keys]);

  return success;
};

export default useKonamiCode;
